// The ONLY place the shell learns about feature areas. Adding an area = one import + one array entry.
import { createElement } from "react";
import type { RouteObject } from "react-router-dom";

import { RequireRoles } from "@/shell/guards";
import type { Surface } from "@/shell/roles";

import * as audit from "./audit";
import * as bonus from "./bonus";
import * as dashboard from "./dashboard";
import * as docs from "./docs";
import * as finance from "./finance";
import * as games from "./games";
import * as leaderboards from "./leaderboards";
import * as mail from "./mail";
import * as p2p from "./p2p";
import * as partners from "./partners";
import * as portal from "./portal";
import * as reports from "./reports";
import * as rounds from "./rounds";
import * as staff from "./staff";
import type { FeatureArea, FeatureRoute } from "./types";

const areas: FeatureArea[] = [
  dashboard,
  partners,
  games,
  bonus,
  reports,
  finance,
  rounds,
  p2p,
  audit,
  staff,
  leaderboards,
  mail,
  docs,
  portal,
];

export const navItems = areas.flatMap((a) => a.nav);

const surfaceOfPath = (path: string): Surface | null =>
  path === "/admin" || path.startsWith("/admin/") ? "admin"
  : path === "/portal" || path.startsWith("/portal/") ? "portal"
  : null;

function toRoute(r: FeatureRoute, surface: Surface): RouteObject {
  const element = createElement(RequireRoles, { roles: r.roles, children: r.element });
  const rel = r.path.slice(`/${surface}`.length).replace(/^\//, "");
  return rel ? { path: rel, element, children: r.children } : { index: true, element };
}

/** Routes of one surface, relative to `/admin` or `/portal`. */
export function routesFor(surface: Surface): RouteObject[] {
  const all = areas.flatMap((a) => a.routes);
  for (const r of all) {
    if (!surfaceOfPath(r.path)) throw new Error(`Feature route ${r.path} must start with /admin or /portal`);
  }
  return all.filter((r) => surfaceOfPath(r.path) === surface).map((r) => toRoute(r, surface));
}
